import { useState, useEffect, useCallback } from 'react';
import { Activity, Loader2, RefreshCw, AlertTriangle, Zap, DollarSign, Wallet, ShieldAlert, Hourglass } from 'lucide-react';
import api from '@/lib/api';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';

// ---------------------------------------------------------------------------
// Types (mirror functions/src/services/ai/inference-stats.ts)
// ---------------------------------------------------------------------------

interface StatusRow { status: string; count: number; pct: number; }
interface ProviderRow {
  provider: string;
  model: string;
  attempts: number;
  successes: number;
  success_rate: number;
  p50_ms: number;
  p95_ms: number;
  cost_usd: number;
  cost_per_success: number;
}
interface LatencyRow { capability: string; successes: number; p50_ms: number; p95_ms: number; }
interface GapRow { capability: string; tenant_site_id: string; count: number; }
interface Intelligence {
  total: number;
  auto_capability_upgrades: number;
  tool_selection_fired: number;
  cost_tier: { free: number; paid: number; other: number };
}
interface CostSummary { total_usd: number; by_provider: { provider: string; cost_usd: number; calls: number }[]; }
interface WastedFirstAttempt { provider: string; model: string; error_type: string; failures: number; sample_error_message?: string | null; }
interface ErrorTypeRow { provider: string; model: string; error_type: string; count: number; }

// Error classes that point at our-side misconfig vs. genuinely provider-side limits.
const OUR_SIDE_ERRORS = new Set(['model_error', 'validation_error', 'empty_response', 'auth_failed']);
// mirror functions/src/services/ai/account-status.ts AccountStatusView
interface AccountRow {
  provider_id: string;
  provider_type: string;
  provider_name: string | null;
  balance_usd: number | null;
  credits_remaining: number | null;
  quota_used: number | null;
  quota_limit: number | null;
  quota_pct: number | null;
  tier: string | null;
  currency: string | null;
  refresh_interval: string | null;
  refresh_at: string | null;
  is_exhausted: boolean;
  source: string;
  fetched_at: string;
}

// mirror functions/src/routes/admin/ai-observability.ts CircuitView / TaskSkipView
interface CircuitRow {
  provider_id: string;
  provider_name: string | null;
  provider_type: string | null;
  source: 'memory' | 'db' | 'memory+db';
  open: boolean;
  failures: number;
  error_type: string;
  cooldown_ms: number;
  cooldown_remaining_ms: number;
  last_failure: string | null;
  deepseek_exemption?: { may_bypass: boolean; reason: string };
}
interface TaskSkipRow {
  provider_id: string;
  provider_name: string | null;
  task_type: string;
  failures: number;
  skipped: boolean;
  cooldown_remaining_ms: number;
}

// mirror functions/src/services/deferred-jobs.ts DeferredQueueStats
interface DeferredQueueJob {
  id: string;
  kind: string;
  status: string;
  attempts: number;
  maxAttempts: number;
  notBefore: string;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}
interface DeferredQueueStats {
  pending: number;
  running: number;
  dueNow: number;
  oldestPendingAgeSec: number | null;
  nextDue: string | null;
  doneRecent: number;
  abandonedRecent: number;
  byKind: { kind: string; pending: number; running: number; done: number; abandoned: number }[];
  jobs: DeferredQueueJob[];
}

// Friendly label for a queue job `kind` (only producer today is the logo loop).
const QUEUE_KIND_LABELS: Record<string, string> = {
  'wizard-logo': 'Logo generation',
};
function queueKindLabel(kind: string): string {
  return QUEUE_KIND_LABELS[kind] || kind;
}

// Status → tenant-facing badge for a deferred job.
function queueStatusBadge(status: string): { label: string; cls: string } {
  switch (status) {
    case 'pending':
      return { label: 'queued', cls: 'bg-amber-100 text-amber-700' };
    case 'running':
      return { label: 'running', cls: 'bg-blue-100 text-blue-700' };
    case 'done':
      return { label: 'done', cls: 'bg-green-100 text-green-700' };
    case 'abandoned':
      return { label: 'gave up', cls: 'bg-red-100 text-red-700' };
    default:
      return { label: status, cls: 'bg-gray-100 text-gray-600' };
  }
}

const WINDOWS = [
  { value: '24', label: 'Last 24 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: '336', label: 'Last 14 days' },
  { value: '720', label: 'Last 30 days' },
];

// Dispatch surface filter. Streaming has different failure semantics
// (commit-then-pipe; failed_midstream) so separating the two makes
// stream-specific reliability/latency problems visible.
const TRANSPORTS = [
  { value: 'all', label: 'All surfaces' },
  { value: 'infer', label: 'Infer only' },
  { value: 'stream', label: 'Streaming only' },
];

function ms(n: number): string {
  if (!n) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}s` : `${Math.round(n)}ms`;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

// Account balance — show the unit the provider denominates in.
function balanceLabel(a: AccountRow): string {
  if (a.balance_usd !== null) return `$${a.balance_usd.toFixed(2)}`;
  if (a.credits_remaining !== null) {
    return a.currency ? `${a.credits_remaining} ${a.currency}` : `${a.credits_remaining}`;
  }
  return '—';
}

// "in 5h" / "in 12d" until a refreshing allotment replenishes; '—' for none.
function untilRefresh(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'due';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `in ${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

// "42m left" / "3h left" until a circuit cooldown elapses.
function cooldownLeft(remainingMs: number): string {
  if (remainingMs <= 0) return '—';
  const mins = Math.ceil(remainingMs / 60000);
  if (mins < 60) return `${mins}m left`;
  const hours = Math.round(mins / 60);
  return `${hours}h left`;
}

// Latency is the headline problem — flag anything slow so it's obvious.
function latencyClass(p50: number): string {
  if (p50 >= 15000) return 'text-red-600 font-semibold';
  if (p50 >= 8000) return 'text-amber-600 font-medium';
  return 'text-gray-700';
}

export default function AiObservabilitySection() {
  const [hours, setHours] = useState('336');
  const [transport, setTransport] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusRow[]>([]);
  const [providers, setProviders] = useState<ProviderRow[]>([]);
  const [latency, setLatency] = useState<LatencyRow[]>([]);
  const [gaps, setGaps] = useState<GapRow[]>([]);
  const [intel, setIntel] = useState<Intelligence | null>(null);
  const [cost, setCost] = useState<CostSummary | null>(null);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [wasted, setWasted] = useState<WastedFirstAttempt[]>([]);
  const [errorTypes, setErrorTypes] = useState<ErrorTypeRow[]>([]);
  const [circuits, setCircuits] = useState<CircuitRow[]>([]);
  const [taskSkips, setTaskSkips] = useState<TaskSkipRow[]>([]);
  const [queue, setQueue] = useState<DeferredQueueStats | null>(null);
  const [resetting, setResetting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = `?hours=${hours}${transport !== 'all' ? `&transport=${transport}` : ''}`;
      const [summary, prov, lat, acct, circ, dq] = await Promise.all([
        api.get<{ status: StatusRow[]; intelligence: Intelligence; cost: CostSummary; gaps: GapRow[]; wastedFirstAttempts: WastedFirstAttempt[]; errorTypes: ErrorTypeRow[] }>(
          `/admin/ai/observability/summary${qs}`,
        ),
        api.get<{ providers: ProviderRow[] }>(`/admin/ai/observability/providers${qs}`),
        api.get<{ latency: LatencyRow[] }>(`/admin/ai/observability/latency${qs}`),
        // Account status is point-in-time (not windowed), so it ignores `qs`.
        api.get<{ accounts: AccountRow[] }>(`/admin/ai/observability/accounts`),
        // Circuit state is point-in-time too.
        api.get<{ circuits: CircuitRow[]; task_skips: TaskSkipRow[] }>(`/admin/ai/observability/circuits`),
        // Self-healing queue: pending/running are point-in-time, done/abandoned use the window.
        api.get<{ queue: DeferredQueueStats }>(`/admin/ai/observability/deferred-queue${qs}`),
      ]);
      setStatus(summary.status);
      setIntel(summary.intelligence);
      setCost(summary.cost);
      setGaps(summary.gaps);
      setWasted(summary.wastedFirstAttempts ?? []);
      setErrorTypes(summary.errorTypes ?? []);
      setProviders(prov.providers);
      setLatency(lat.latency);
      setAccounts(acct.accounts ?? []);
      setCircuits(circ.circuits ?? []);
      setTaskSkips(circ.task_skips ?? []);
      setQueue(dq.queue ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load RPAAD analytics');
    } finally {
      setLoading(false);
    }
  }, [hours, transport]);

  useEffect(() => {
    load();
  }, [load]);

  const resetCircuit = async (providerId: string) => {
    setResetting(providerId);
    try {
      await api.post(`/admin/ai/observability/circuits/${providerId}/reset`, {});
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset circuit');
    } finally {
      setResetting(null);
    }
  };

  const totalCalls = status.reduce((a, b) => a + b.count, 0);
  const successPct = status.find((s) => s.status === 'success')?.pct ?? 0;
  const noProviders = status.find((s) => s.status === 'no_providers');
  const allFailed = status.find((s) => s.status === 'all_failed');
  const hasQueue = !!queue && queue.pending + queue.running + queue.doneRecent + queue.abandonedRecent > 0;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-[var(--color-sage-600,#4b7355)]" aria-hidden="true" />
          <h3 className="text-lg font-semibold text-gray-900">AI Dispatcher Analytics (RPAAD)</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={transport}
            onChange={(e) => setTransport(e.target.value)}
            className="w-40"
            options={TRANSPORTS}
            aria-label="Dispatch surface"
          />
          <Select
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="w-44"
            options={WINDOWS}
          />
          <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        How the dispatcher is routing this tenant&apos;s AI calls — which providers win, how reliable and
        fast they are, what they cost, and where coverage is missing.
      </p>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Account & credits — independent of dispatcher activity. Empty until
          RPAAD_ACCOUNT_STATUS is enabled and the account cron has run. */}
      {accounts.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="w-4 h-4 text-emerald-600" aria-hidden="true" />
            <h4 className="text-sm font-semibold text-gray-800">Account &amp; credits</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Provider</th>
                  <th className="py-2 px-2 font-medium">Tier</th>
                  <th className="py-2 px-2 font-medium text-right">Balance</th>
                  <th className="py-2 px-2 font-medium text-right">Quota</th>
                  <th className="py-2 px-2 font-medium text-right">Refreshes</th>
                  <th className="py-2 pl-2 font-medium text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => {
                  const lowBalance = a.balance_usd !== null && a.balance_usd <= 1 && !a.is_exhausted;
                  const lowQuota = a.quota_pct !== null && a.quota_pct >= 0.9 && !a.is_exhausted;
                  return (
                    <tr key={a.provider_id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-gray-900">
                        {a.provider_name || a.provider_type}
                        {a.provider_name && (
                          <span className="text-gray-400 font-mono text-xs"> · {a.provider_type}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-gray-700 capitalize">{a.tier || '—'}</td>
                      <td className={`py-2 px-2 text-right ${lowBalance ? 'text-amber-600 font-medium' : 'text-gray-700'}`}>
                        {balanceLabel(a)}
                      </td>
                      <td className={`py-2 px-2 text-right ${lowQuota ? 'text-amber-600 font-medium' : 'text-gray-700'}`}>
                        {a.quota_limit !== null
                          ? `${a.quota_pct !== null ? pct(a.quota_pct) : '—'}`
                          : '—'}
                      </td>
                      <td className="py-2 px-2 text-right text-gray-700">{untilRefresh(a.refresh_at)}</td>
                      <td className="py-2 pl-2 text-right">
                        {a.is_exhausted ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium">
                            exhausted
                          </span>
                        ) : lowBalance || lowQuota ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-medium">
                            low
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-green-100 text-green-700 px-2 py-0.5 text-xs font-medium">
                            ok
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Circuit breakers — why a provider is currently being skipped. Point-in-time. */}
      {(circuits.length > 0 || taskSkips.length > 0) && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-4 h-4 text-red-500" aria-hidden="true" />
            <h4 className="text-sm font-semibold text-gray-800">Circuit breakers</h4>
            {circuits.some((cb) => cb.open) && (
              <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium">
                {circuits.filter((cb) => cb.open).length} open
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Providers the dispatcher is skipping (or counting failures against) and why. Reset
            re-enables a provider immediately — if it&apos;s genuinely broken the circuit just re-opens.
          </p>
          {circuits.length > 0 && (
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Provider</th>
                    <th className="py-2 px-2 font-medium">State</th>
                    <th className="py-2 px-2 font-medium">Error</th>
                    <th className="py-2 px-2 font-medium text-right">Failures</th>
                    <th className="py-2 px-2 font-medium text-right">Cooldown</th>
                    <th className="py-2 pl-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {circuits.map((cb) => (
                    <tr key={cb.provider_id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-gray-900">
                        {cb.provider_name || cb.provider_type || cb.provider_id}
                        {cb.provider_type && (
                          <span className="text-gray-400 font-mono text-xs"> · {cb.provider_type}</span>
                        )}
                        {cb.deepseek_exemption && (
                          <div
                            className="text-xs text-amber-700 mt-0.5"
                            title={cb.deepseek_exemption.reason}
                          >
                            {cb.deepseek_exemption.may_bypass
                              ? 'still tried (paid-fallback exemption)'
                              : 'exemption disabled (auth error)'}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {cb.open ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium">
                            open
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-medium">
                            counting ({cb.failures})
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-2 font-mono text-xs text-gray-700">{cb.error_type}</td>
                      <td className="py-2 px-2 text-right text-gray-700">{cb.failures}</td>
                      <td className="py-2 px-2 text-right text-gray-700">
                        {cb.open ? cooldownLeft(cb.cooldown_remaining_ms) : '—'}
                      </td>
                      <td className="py-2 pl-2 text-right">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => resetCircuit(cb.provider_id)}
                          disabled={resetting === cb.provider_id}
                        >
                          {resetting === cb.provider_id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            'Reset'
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {taskSkips.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Provider</th>
                    <th className="py-2 px-2 font-medium">Task</th>
                    <th className="py-2 px-2 font-medium text-right">Failures</th>
                    <th className="py-2 px-2 font-medium">State</th>
                    <th className="py-2 pl-2 font-medium text-right">Cooldown</th>
                  </tr>
                </thead>
                <tbody>
                  {taskSkips.map((t) => (
                    <tr key={`${t.provider_id}:${t.task_type}`} className="border-b border-gray-100">
                      <td className="py-2 pr-4 text-gray-900">{t.provider_name || t.provider_id}</td>
                      <td className="py-2 px-2 text-gray-700 capitalize">{t.task_type}</td>
                      <td className="py-2 px-2 text-right text-gray-700">{t.failures}</td>
                      <td className="py-2 px-2">
                        {t.skipped ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-medium">
                            skipped
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-medium">
                            counting
                          </span>
                        )}
                      </td>
                      <td className="py-2 pl-2 text-right text-gray-700">
                        {t.skipped ? cooldownLeft(t.cooldown_remaining_ms) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Self-healing queue — async AI work waiting for provider credits to
          refresh, so a deferred request (e.g. logo generation) is visible
          instead of silent limbo. Hidden when the queue is empty. */}
      {hasQueue && queue && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Hourglass className="w-4 h-4 text-amber-500" aria-hidden="true" />
            <h4 className="text-sm font-semibold text-gray-800">Self-healing queue</h4>
            {queue.abandonedRecent > 0 && (
              <span className="inline-flex items-center rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-medium">
                {queue.abandonedRecent} gave up
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-2">
            AI work that paused because every suitable provider was out of credits. It retries
            automatically once credits refresh — nothing is lost.
            {queue.pending > 0 && queue.nextDue && (
              <> Next retry {untilRefresh(queue.nextDue)}.</>
            )}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <Stat label="Queued" value={String(queue.pending)} tone={queue.pending > 0 ? 'warn' : undefined} />
            <Stat label="Running" value={String(queue.running)} />
            <Stat label="Due now" value={String(queue.dueNow)} tone={queue.dueNow > 0 ? 'warn' : undefined} />
            <Stat label={`Gave up (${hours}h)`} value={String(queue.abandonedRecent)} tone={queue.abandonedRecent > 0 ? 'bad' : 'good'} />
          </div>
          {queue.jobs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4 font-medium">Work</th>
                    <th className="py-2 px-2 font-medium">State</th>
                    <th className="py-2 px-2 font-medium text-right">Attempt</th>
                    <th className="py-2 px-2 font-medium text-right">Next retry</th>
                    <th className="py-2 pl-2 font-medium">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.jobs.map((j) => {
                    const badge = queueStatusBadge(j.status);
                    return (
                      <tr key={j.id} className="border-b border-gray-100">
                        <td className="py-2 pr-4 text-gray-900">{queueKindLabel(j.kind)}</td>
                        <td className="py-2 px-2">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-right text-gray-700">{j.attempts}/{j.maxAttempts}</td>
                        <td className="py-2 px-2 text-right text-gray-700">
                          {j.status === 'pending' ? untilRefresh(j.notBefore) : '—'}
                        </td>
                        <td className="py-2 pl-2 text-gray-500 max-w-xs truncate" title={j.lastError ?? ''}>
                          {j.lastError ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && totalCalls === 0 && accounts.length === 0 && circuits.length === 0 && !hasQueue && !error && (
        <p className="text-sm text-gray-500">No dispatcher activity in this window yet.</p>
      )}

      {totalCalls > 0 && (
        <>
          {/* Health header */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <Stat label="Total calls" value={totalCalls.toLocaleString()} />
            <Stat label="Success rate" value={`${successPct}%`} tone={successPct >= 95 ? 'good' : successPct >= 85 ? 'warn' : 'bad'} />
            <Stat label="No provider" value={`${noProviders?.count ?? 0} (${noProviders?.pct ?? 0}%)`} tone={(noProviders?.count ?? 0) > 0 ? 'warn' : 'good'} />
            <Stat label="All failed" value={`${allFailed?.count ?? 0} (${allFailed?.pct ?? 0}%)`} tone={(allFailed?.count ?? 0) > 0 ? 'warn' : 'good'} />
          </div>

          {/* Provider scoreboard */}
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Provider scoreboard</h4>
          <div className="overflow-x-auto mb-6">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4 font-medium">Provider / model</th>
                  <th className="py-2 px-2 font-medium text-right">Attempts</th>
                  <th className="py-2 px-2 font-medium text-right">Success</th>
                  <th className="py-2 px-2 font-medium text-right">p50</th>
                  <th className="py-2 px-2 font-medium text-right">p95</th>
                  <th className="py-2 px-2 font-medium text-right">Cost</th>
                  <th className="py-2 pl-2 font-medium text-right">$/success</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={`${p.provider}/${p.model}`} className="border-b border-gray-100">
                    <td className="py-2 pr-4 text-gray-900 font-mono text-xs">{p.provider}/{p.model || '—'}</td>
                    <td className="py-2 px-2 text-right text-gray-700">{p.attempts}</td>
                    <td className="py-2 px-2 text-right text-gray-700">{pct(p.success_rate)}</td>
                    <td className={`py-2 px-2 text-right ${latencyClass(p.p50_ms)}`}>{ms(p.p50_ms)}</td>
                    <td className={`py-2 px-2 text-right ${latencyClass(p.p95_ms)}`}>{ms(p.p95_ms)}</td>
                    <td className="py-2 px-2 text-right text-gray-700">${p.cost_usd.toFixed(4)}</td>
                    <td className="py-2 pl-2 text-right text-gray-700">${p.cost_per_success.toFixed(5)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Failover-masked breakage: the failing #1 candidate burns the top
              routing slot on every call. Invisible on the success rate (failover
              rescues the call) but usually the highest-leverage fix — an our-side
              error here (model_error etc.) is almost always a config bug. */}
          {wasted.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-500" aria-hidden="true" />
                <h4 className="text-sm font-semibold text-gray-800">Wasted first attempts</h4>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                The #1 candidate failed and forced a failover. An our-side error type (model_error,
                validation_error, empty_response, auth_failed) points at a fixable config bug, not a dead provider.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4 font-medium">Provider / model</th>
                      <th className="py-2 px-2 font-medium">Error</th>
                      <th className="py-2 pl-2 font-medium text-right">Failures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wasted.map((w, i) => {
                      const ourSide = OUR_SIDE_ERRORS.has(w.error_type);
                      return (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 pr-4 text-gray-900 font-mono text-xs align-top">
                            {w.provider}/{w.model || '—'}
                            {/* The provider's own words. error_type groups these failures;
                                this explains one — a 413 size rejection and a malformed
                                payload both classify as invalid_request. */}
                            {w.sample_error_message && (
                              <div
                                className="mt-1 max-w-md break-words font-sans text-[11px] leading-snug text-gray-500"
                                title={w.sample_error_message}
                              >
                                {w.sample_error_message}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-2 align-top">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ourSide ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                              {w.error_type}{ourSide ? ' · fix config' : ''}
                            </span>
                          </td>
                          <td className="py-2 pl-2 text-right text-gray-700 align-top">{w.failures}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All non-winning attempts by provider / error type — the broader
              view behind "wasted first attempts": separates our-side config bugs
              from genuinely provider-side limits across every failover slot.
              Already shipped by /summary; previously computed but never rendered. */}
          {errorTypes.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" aria-hidden="true" />
                <h4 className="text-sm font-semibold text-gray-800">Failed attempts by provider</h4>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Every non-winning attempt, grouped by provider and error type. Our-side errors
                (model_error, validation_error, empty_response, auth_failed) point at a config bug;
                provider-side ones (quota_exhausted, rate_limited) are expected and handled by failover.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-4 font-medium">Provider / model</th>
                      <th className="py-2 px-2 font-medium">Error</th>
                      <th className="py-2 pl-2 font-medium text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorTypes.map((e, i) => {
                      const ourSide = OUR_SIDE_ERRORS.has(e.error_type);
                      return (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 pr-4 text-gray-900 font-mono text-xs">{e.provider}/{e.model || '—'}</td>
                          <td className="py-2 px-2">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ourSide ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                              {e.error_type}{ourSide ? ' · fix config' : ''}
                            </span>
                          </td>
                          <td className="py-2 pl-2 text-right text-gray-700">{e.count}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Latency by capability */}
          {latency.length > 0 && (
            <>
              <h4 className="text-sm font-semibold text-gray-800 mb-2">Latency by capability</h4>
              <div className="flex flex-wrap gap-3 mb-6">
                {latency.map((l) => (
                  <div key={l.capability} className="rounded-md border border-gray-200 px-3 py-2">
                    <div className="text-xs text-gray-500 capitalize">{l.capability} ({l.successes})</div>
                    <div className="text-sm">
                      <span className={latencyClass(l.p50_ms)}>p50 {ms(l.p50_ms)}</span>
                      <span className="text-gray-400"> · </span>
                      <span className={latencyClass(l.p95_ms)}>p95 {ms(l.p95_ms)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Bottom row: gaps + intelligence + cost */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Panel icon={<AlertTriangle className="w-4 h-4 text-amber-500" />} title="Coverage gaps">
              {gaps.length === 0 ? (
                <p className="text-sm text-gray-500">None — every call found a provider.</p>
              ) : (
                <ul className="text-sm text-gray-700 space-y-1">
                  {gaps.map((g, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="capitalize">{g.capability}</span>
                      <span className="text-amber-600 font-medium">{g.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel icon={<Zap className="w-4 h-4 text-indigo-500" />} title="Intelligence firing">
              {intel && (
                <ul className="text-sm text-gray-700 space-y-1">
                  <li className="flex justify-between"><span>Auto capability upgrade</span><span>{intel.auto_capability_upgrades}</span></li>
                  <li className="flex justify-between"><span>Tool selection</span><span>{intel.tool_selection_fired}</span></li>
                  <li className="flex justify-between"><span>Free / paid tier</span><span>{intel.cost_tier.free} / {intel.cost_tier.paid}</span></li>
                </ul>
              )}
            </Panel>

            <Panel icon={<DollarSign className="w-4 h-4 text-green-600" />} title="Cost (estimated)">
              {cost && (
                <>
                  <div className="text-lg font-semibold text-gray-900 mb-1">${cost.total_usd.toFixed(4)}</div>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {cost.by_provider.slice(0, 4).map((c) => (
                      <li key={c.provider} className="flex justify-between">
                        <span>{c.provider}</span>
                        <span>${c.cost_usd.toFixed(4)}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Panel>
          </div>
        </>
      )}
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'warn' | 'bad' }) {
  const toneClass =
    tone === 'good' ? 'text-green-700' : tone === 'warn' ? 'text-amber-700' : tone === 'bad' ? 'text-red-700' : 'text-gray-900';
  return (
    <div className="rounded-md border border-gray-200 px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}

function Panel({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm font-semibold text-gray-800">{title}</span>
      </div>
      {children}
    </div>
  );
}
